/**
 * Parallel Study Card Component
 *
 * Displays a group of parallel studies that share the same parallel group ID.
 * Shows a collapsed view with aggregate metrics and expands to show individual studies.
 */

import { format, parseISO } from "date-fns";
import {
  ArrowUpRight,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  GitCompare,
  Layers,
  MoreVertical,
  Trophy
} from "lucide-react";
import { useCallback, useState } from "react";

import { formatStrictWithApprox, parseApproxConfigFromToml } from "@/components/results/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Study } from "@/types/api";
import { cn } from "@/utils/cn";
import {
  type ParallelGroupStatus,
  type ParallelStudyGroup,
  calculateStudyAge,
  enrichStudyWithMetadata,
  formatAge,
  getFreshnessColorClass,
  getFreshnessDescription,
  getFreshnessStatus,
} from "@/utils/studyGrouping";

// =============================================================================
// Types
// =============================================================================

interface ParallelStudyCardProps {
  /** The parallel study group to display */
  parallelGroup: ParallelStudyGroup;
  selectedStudyIds: Set<string>;
  onStudyClick: (study: Study) => void;
  onStudySelect: (studyId: string) => void;
  onCompareAll: (studies: Study[]) => void;
  /** @deprecated Use parallelGroup instead */
  batch?: ParallelStudyGroup;
}

// =============================================================================
// Status Badge
// =============================================================================

function StatusBadge({ status }: { status: ParallelGroupStatus }) {
  const statusConfig = {
    queued: { label: "Queued", className: "bg-sky-500/10 text-sky-400 border-sky-500/30" },
    running: { label: "Running", className: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
    completed: { label: "Completed", className: "bg-green-500/10 text-green-400 border-green-500/30" },
    failed: { label: "Failed", className: "bg-red-500/10 text-red-400 border-red-500/30" },
    cancelled: { label: "Cancelled", className: "bg-gray-500/10 text-gray-400 border-gray-500/30" },
    paused: { label: "Paused", className: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" },
    mixed: { label: "Mixed", className: "bg-purple-500/10 text-purple-400 border-purple-500/30" },
  };

  const config = statusConfig[status];

  return (
    <Badge variant="outline" className={cn("text-xs", config.className)}>
      {config.label}
    </Badge>
  );
}

// =============================================================================
// Individual Study Row (for expanded view)
// =============================================================================

interface StudyRowProps {
  study: Study;
  isWinner: boolean;
  isSelected: boolean;
  batchIndex: number;
  batchTotal: number;
  onSelect: () => void;
  onClick: () => void;
}

function StudyRow({
  study,
  isWinner,
  isSelected,
  batchIndex,
  batchTotal,
  onSelect,
  onClick,
}: StudyRowProps) {
  // AIE-319: append approx real Sortino/Sharpe alongside the strict best value
  const bestValue = formatStrictWithApprox(
    study.best_value,
    study.total_trades,
    parseApproxConfigFromToml(study.toml_config),
  );
  const enriched = enrichStudyWithMetadata(study);
  const ageDisplay = formatAge(enriched.ageInDays);
  const freshnessClass = getFreshnessColorClass(enriched.freshness);
  const freshnessDesc = getFreshnessDescription(enriched.freshness, enriched.ageInDays);

  return (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md transition-colors cursor-pointer",
        "hover:bg-dark-800/50",
        isSelected && "bg-blue-500/10 border-blue-500/30"
      )}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {/* Selection checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className={cn(
          "w-4 h-4 rounded border flex items-center justify-center transition-all shrink-0",
          isSelected ? "bg-blue-500 border-blue-500" : "border-dark-600"
        )}
      >
        {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
      </button>

      {/* Version indicator */}
      <span className="text-xs text-dark-500 font-mono w-10 shrink-0">
        v{batchIndex}/{batchTotal}
      </span>

      {/* Winner badge */}
      <div className="w-5 shrink-0">
        {isWinner && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Trophy className="h-4 w-4 text-yellow-500" />
              </TooltipTrigger>
              <TooltipContent>Best performing version</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Best value */}
      <span className={cn("font-mono text-sm flex-1", isWinner && "text-yellow-400")}>
        {bestValue}
      </span>

      {/* Trials progress bar */}
      <div className="flex-1 max-w-[120px] px-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="w-full">
                <Progress
                  value={Math.min(((study.n_trials_completed ?? 0) / (study.n_trials ?? 1)) * 100, 100)}
                  variant={study.status === 'completed' ? 'success' : study.status === 'failed' ? 'danger' : 'brand'}
                  className="h-1"
                  animate={false}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {Math.min(study.n_trials_completed ?? 0, study.n_trials ?? 0)}/{study.n_trials ?? 0} trials completed
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Age badge */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn(
              "text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider", 
              freshnessClass,
              ageDisplay === "Today" && "bg-green-500/20 text-green-400 border-green-500/40 shadow-[0_0_10px_rgba(34,197,94,0.15)] pulse-subtle"
            )}>
              {ageDisplay}
            </span>
          </TooltipTrigger>
          <TooltipContent>{freshnessDesc}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* View button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        <ArrowUpRight className="h-3 w-3" />
      </Button>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function ParallelStudyCard({
  parallelGroup,
  batch, // deprecated prop for backward compatibility
  selectedStudyIds,
  onStudyClick,
  onStudySelect,
  onCompareAll,
}: ParallelStudyCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Support both new and legacy prop names
  const group = parallelGroup ?? batch;

  // Guard clause: group must have at least one study
  const firstStudy = group?.studies[0];
  if (!group || !firstStudy) {
    return null;
  }

  // Calculate aggregate stats
  const ageInDays = calculateStudyAge(firstStudy);
  const freshness = getFreshnessStatus(ageInDays);
  const freshnessClass = getFreshnessColorClass(freshness);
  const freshnessDesc = getFreshnessDescription(freshness, ageInDays);
  const ageDisplay = formatAge(ageInDays);

  const createdDate = firstStudy.created_at
    ? format(parseISO(firstStudy.created_at), "MMM d, yyyy")
    : "Unknown";

  const totalTrials = group.studies.reduce((sum, s) => sum + (s.n_trials ?? 0), 0);
  const completedTrials = group.studies.reduce((sum, s) => sum + (s.n_trials_completed ?? 0), 0);

  // Check how many are selected
  const selectedCount = group.studies.filter((s) => selectedStudyIds.has(s.study_id)).length;
  const allSelected = selectedCount === group.studies.length;
  const someSelected = selectedCount > 0 && selectedCount < group.studies.length;

  // Handle select all/none
  const handleSelectAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (allSelected) {
        // Deselect all
        group.studies.forEach((s) => {
          if (selectedStudyIds.has(s.study_id)) {
            onStudySelect(s.study_id);
          }
        });
      } else {
        // Select all
        group.studies.forEach((s) => {
          if (!selectedStudyIds.has(s.study_id)) {
            onStudySelect(s.study_id);
          }
        });
      }
    },
    [group.studies, selectedStudyIds, allSelected, onStudySelect]
  );

  // Handle compare all
  const handleCompareAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCompareAll(group.studies);
    },
    [group.studies, onCompareAll]
  );

  // Toggle expansion
  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Status-based colors matching solo study cards
  const getStatusColors = (status: ParallelGroupStatus) => {
    switch (status) {
      case "queued":
        return {
          gradient: "from-sky-500/20 to-sky-600/20",
          border: "border-sky-500/30",
          icon: "text-sky-400",
          iconBg: "bg-sky-500/15 border-sky-500/30",
        };
      case "running":
        return {
          gradient: "from-blue-500/20 to-blue-600/20",
          border: "border-blue-500/30",
          icon: "text-blue-400",
          iconBg: "bg-blue-500/15 border-blue-500/30",
        };
      case "completed":
        return {
          gradient: "from-green-500/20 to-emerald-600/20",
          border: "border-green-500/30",
          icon: "text-green-400",
          iconBg: "bg-green-500/15 border-green-500/30",
        };
      case "failed":
        return {
          gradient: "from-red-500/20 to-red-600/20",
          border: "border-red-500/30",
          icon: "text-red-400",
          iconBg: "bg-red-500/15 border-red-500/30",
        };
      case "paused":
        return {
          gradient: "from-slate-500/20 to-slate-600/20",
          border: "border-slate-500/30",
          icon: "text-slate-400",
          iconBg: "bg-slate-500/15 border-slate-500/30",
        };
      case "cancelled":
        return {
          gradient: "from-gray-500/20 to-gray-600/20",
          border: "border-gray-500/30",
          icon: "text-gray-400",
          iconBg: "bg-gray-500/15 border-gray-500/30",
        };
      case "mixed":
        return {
          gradient: "from-purple-500/20 to-purple-600/20",
          border: "border-purple-500/30",
          icon: "text-purple-400",
          iconBg: "bg-purple-500/15 border-purple-500/30",
        };
      default:
        return {
          gradient: "from-gray-500/20 to-gray-600/20",
          border: "border-gray-500/30",
          icon: "text-gray-400",
          iconBg: "bg-gray-500/15 border-gray-500/30",
        };
    }
  };

  const statusColors = getStatusColors(group.groupStatus);

  return (
    <div
      className={cn(
        "group relative rounded-lg border transition-all hover:shadow-glow",
        statusColors.border,
        "bg-dark-900/20",
        isExpanded && "bg-dark-800/20"
      )}
    >
      {/* Status gradient background */}
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-r opacity-10 rounded-lg pointer-events-none",
          statusColors.gradient
        )}
      />
      {/* Collapsed header */}
      <div
        onClick={handleToggle}
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-dark-800/30 rounded-lg"
      >
        {/* Selection checkbox */}
        <button
          onClick={handleSelectAll}
          className={cn(
            "w-5 h-5 rounded border flex items-center justify-center transition-all shrink-0",
            allSelected
              ? "bg-blue-500 border-blue-500"
              : someSelected
              ? "bg-blue-500/50 border-blue-500"
              : "border-dark-600"
          )}
        >
          {(allSelected || someSelected) && <Check className="h-3 w-3 text-white" />}
        </button>

        {/* Parallel study indicator - color matches status */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded-md border shrink-0",
                statusColors.iconBg
              )}>
                <Layers className={cn("h-4 w-4", statusColors.icon)} />
                <span className={cn("text-xs font-semibold", statusColors.icon)}>
                  {group.parallel_total}×
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{group.parallel_total} parallel studies</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Study info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-bold text-lg text-white truncate tracking-tight">
              {group.baseName.replace(/^sample_/, "").toUpperCase().replace(/_/g, " ")}
            </h4>
            {group.baseName.startsWith("sample_") && (
              <Badge variant="outline" className="text-[10px] py-0 h-4 bg-purple-500/10 border-purple-500/30 text-purple-400">
                Sample
              </Badge>
            )}
            <StatusBadge status={group.groupStatus} />
          </div>
          <div className="flex items-center gap-3 text-xs text-dark-500">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {createdDate}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={cn("px-1.5 py-0.5 rounded border cursor-help", freshnessClass)}>
                    {ageDisplay}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{freshnessDesc}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-dark-600">•</span>
            <div className="flex items-center gap-2">
              <span className="shrink-0">{completedTrials}/{totalTrials} trials</span>
              <div className="w-16">
                <Progress 
                  value={(completedTrials / (totalTrials || 1)) * 100} 
                  variant={group.groupStatus === 'completed' ? 'success' : group.groupStatus === 'failed' ? 'danger' : 'brand'}
                  className="h-1"
                  animate={false}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Best value across parallel group */}
        {group.bestValue !== null && (
          <div className="flex flex-col items-end gap-0.5 shrink-0 px-4">
            <span className="text-[10px] text-dark-500 uppercase tracking-wider font-bold">Best Performance</span>
            <div className="flex items-center gap-1.5">
              <Trophy className="h-4 w-4 text-yellow-500" />
              <span className="font-mono text-base font-bold text-yellow-400">{formatStrictWithApprox(
                group.bestValue,
                group.bestStudy?.total_trades,
                parseApproxConfigFromToml(group.bestStudy?.toml_config),
              )}</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={handleCompareAll}>
                <GitCompare className="h-4 w-4 mr-2" />
                Compare All Versions
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSelectAll}>
                <Check className="h-4 w-4 mr-2" />
                {allSelected ? "Deselect All" : "Select All"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Expand/collapse chevron */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-dark-700/30">
          <div className="mt-3 space-y-1">
            {group.studies.map((study) => (
              <StudyRow
                key={study.study_id}
                study={study}
                isWinner={group.bestStudy?.study_id === study.study_id}
                isSelected={selectedStudyIds.has(study.study_id)}
                batchIndex={study.batch_index ?? 0}
                batchTotal={group.parallel_total}
                onSelect={() => onStudySelect(study.study_id)}
                onClick={() => onStudyClick(study)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParallelStudyCard;
